import { fmtMoney } from '../engine.js';

/* v8 Path U — shared filing-style amount formatter.
   DRY between FormalStatementTable + MetricHorizonTable so comparative
   tables present numbers exactly like the 3-statement model:
     - All amounts white (consumer decides the class; this helper
       is purely about string formatting)
     - Zero ⇒ "—" (em-dash, filing convention)
     - Negatives in parentheses, NOT with a leading minus
     - Positives use the normal money formatter
   `decimals` lets comparative tables render whole-unit values (2-dp
   gets too busy across 12 columns). */
export function fmtAmount(v, parens = true, decimals = 2) {
  if (v == null || v === '') return '';
  if (typeof v !== 'number') return v;
  if (!Number.isFinite(v)) return '';
  if (v === 0) return '—';
  if (v < 0 && parens) return `(${fmtMoney(Math.abs(v), { decimals })})`;
  return fmtMoney(v, { decimals });
}

/* Non-currency formatter for ratio/pct/days/months/x — mirrors the
   existing MetricHorizonTable logic but in one canonical place.
   v8 Path AF.3 — Infinity is a meaningful value for months/days
   (e.g. Cash runway = Infinity when liquid never depletes in the
   48-month projection). Render it as "∞" instead of the em-dash
   fallback used for truly-missing values. null/NaN still em-dash. */
export function fmtMetric(v, format, decimals = 0) {
  if (v == null || Number.isNaN(v)) return '—';
  if (v === Infinity || v === -Infinity) {
    const sign = v === -Infinity ? '−' : '';
    switch (format) {
      case 'months': return `${sign}∞mo`;
      case 'days':   return `${sign}∞d`;
      case 'x':      return `${sign}∞×`;
      case 'pct':    return `${sign}∞%`;
      default:       return `${sign}∞`;
    }
  }
  switch (format) {
    case 'pct':      return `${(v * 100).toFixed(1)}%`;
    case 'days':     return `${Math.round(v)}d`;
    case 'months':   return `${v.toFixed(1)}mo`;
    case 'x':        return `${v.toFixed(2)}×`;
    case 'currency': return fmtAmount(v, true, decimals);
    case 'ratio':
    default:         return v.toFixed(2);
  }
}

/* v8 Path AE — FORMAT-AWARE MATERIALITY.
   The old `max - min < 1` heuristic was currency-biased: for pesos,
   1 unit of spread is dust; for ratios (D/E hovering -1.02→-1.03,
   Debt-to-Assets 0.97→1.02, Savings rate -0.09→-0.31) the same
   threshold hides genuinely material movement.

   Each format has its own "smallest visible movement" floor AND a
   relative-to-magnitude component (materiality concept from GAAP —
   0.5% of the row's typical magnitude counts as meaningful).

   Returns the absolute delta that must be exceeded to be "material".
   Consumer decides flat vs not-flat by comparing against this. */
export function materialityFloor(format) {
  switch (format) {
    case 'pct':      return 0.001;   // 0.1 percentage-point — visible on screen
    case 'ratio':    return 0.005;   // 0.01 × (2nd decimal change in "1.02")
    case 'days':     return 1;       // the formatter rounds to whole days anyway
    case 'months':   return 0.1;     // 0.1mo — matches formatter precision
    case 'x':        return 0.01;    // 0.01× — matches formatter precision
    case 'currency': return 0.5;     // half a peso — preserves prior behavior
    default:         return 0.005;
  }
}

/* v8 Path AE — format-aware flat detection.
   A series is flat when its peak-to-trough spread is below BOTH:
     - the format's absolute floor (materialityFloor), AND
     - a relative threshold (0.5% of typical magnitude).
   "Both" is the right join: a ratio that lives near 0 must clear the
   absolute floor even if 0.5% of its magnitude rounds to zero; a ratio
   that lives near 1000 must also clear the relative bar so a spurious
   0.005 jiggle doesn't count as signal. */
export function isFlatSeries(values, format = 'currency') {
  if (!Array.isArray(values) || values.length < 2) return true;
  const nums = values.filter(v => typeof v === 'number' && Number.isFinite(v));
  if (nums.length < 2) return true;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const spread = max - min;
  const magnitude = Math.max(Math.abs(max), Math.abs(min));
  const floor = materialityFloor(format);
  const relative = magnitude * 0.005;                 // 0.5% of typical value
  // Material when spread exceeds EITHER bar — so genuinely-moving rows
  // near zero (ratio jiggle 0.97→1.02) clear the absolute floor, and
  // genuinely-moving rows at scale clear the relative bar.
  const threshold = Math.min(floor, Math.max(floor * 0.5, relative));
  return spread <= threshold;
}

/* ============================================================
   TREND QUALITY SCORE (TQS) — implements I-001 invention.
   See 3-resources/canon/inventions/I-001-trend-quality-score.md.

   Four orthogonal component scorers combined via geometric-mean
   consensus, weighted by sample-size confidence:

     TQS = confidence_factor × geomean(persistence, linearity, robust_snr)

   Range [0, 1]. Threshold 0.35 separates "colour it" from "grey it."
   ============================================================ */

export const TQS_FLAT_THRESHOLD = 0.35;
export const TQS_CONFIDENCE_N = 10;     // n where confidence_factor reaches 1

/* Sum of absolute step-to-step changes. Denominator of Kaufman Efficiency
   Ratio. O(n). */
export function pathLength(nums) {
  if (!Array.isArray(nums) || nums.length < 2) return 0;
  let sum = 0;
  for (let i = 1; i < nums.length; i++) {
    sum += Math.abs(nums[i] - nums[i - 1]);
  }
  return sum;
}

/* Coefficient of determination (R²) for a linear fit against uniform
   x-spacing. Returns [0, 1]. 0 when the series has zero variance (flat)
   or zero x-variance (shouldn't happen for n >= 2). */
export function rSquared(nums) {
  if (!Array.isArray(nums) || nums.length < 2) return 0;
  const n = nums.length;
  const xMean = (n - 1) / 2;
  let yMean = 0;
  for (const v of nums) yMean += v;
  yMean /= n;
  let ssXY = 0, ssXX = 0, ssYY = 0;
  for (let i = 0; i < n; i++) {
    const dx = i - xMean;
    const dy = nums[i] - yMean;
    ssXY += dx * dy;
    ssXX += dx * dx;
    ssYY += dy * dy;
  }
  if (ssXX === 0 || ssYY === 0) return 0;
  const r = ssXY / Math.sqrt(ssXX * ssYY);
  return r * r;
}

/* Linear-interpolated percentile (standard quantile). Not nearest-rank.
   p is a fraction in [0, 1]. Resists single-outlier pollution better than
   raw min/max when used as P5 / P95. */
export function percentile(values, p) {
  if (!Array.isArray(values) || values.length === 0) return NaN;
  const sorted = values.filter(v => Number.isFinite(v)).sort((a, b) => a - b);
  if (sorted.length === 0) return NaN;
  if (sorted.length === 1) return sorted[0];
  const idx = p * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  const frac = idx - lo;
  return sorted[lo] * (1 - frac) + sorted[hi] * frac;
}

/* Trend Quality Score — per I-001.

   Returns an object so callers can inspect component scores for
   debugging / diagnostic UI, not just the final verdict:

     { tqs, persistence, linearity, robust_snr, confidence_factor }

   All fields are in [0, 1]. tqs = confidence_factor × geomean(content).
   geomean collapses to 0 when any content component is 0 — that's the
   consensus design: all three must agree for the series to earn colour. */
export function trendQualityScore(values, format = 'currency') {
  const empty = { tqs: 0, persistence: 0, linearity: 0, robust_snr: 0, confidence_factor: 0 };
  if (!Array.isArray(values) || values.length < 2) return empty;
  const nums = values.filter(v => typeof v === 'number' && Number.isFinite(v));
  if (nums.length < 2) return empty;

  const first = nums[0];
  const last  = nums[nums.length - 1];
  const delta = Math.abs(last - first);

  /* Persistence = Kaufman Efficiency Ratio. Bounded [0, 1].
     How direct was the path — net delta over total step-length. */
  const pathLen = pathLength(nums);
  const persistence = pathLen <= 0 ? 0 : Math.min(1, delta / pathLen);

  /* Linearity = R² of linear regression. [0, 1]. */
  const linearity = rSquared(nums);

  /* Robust SNR = |delta| / (P95 − P5). Robust to a single outlier that
     would otherwise inflate max-min. Capped at 1. */
  const p5  = percentile(nums, 0.05);
  const p95 = percentile(nums, 0.95);
  const robustSpread = p95 - p5;
  const robust_snr = robustSpread <= 0 ? 0 : Math.min(1, delta / robustSpread);

  /* Sample-size confidence. Scales smoothly 0 → 1 as n → N_target. */
  const confidence_factor = Math.min(1, nums.length / TQS_CONFIDENCE_N);

  /* Geomean consensus — cube root of the product of three content scores.
     If any component is 0, the geomean is 0 (no matter how high the
     others score). This is the invention's core consensus logic. */
  const content = Math.cbrt(persistence * linearity * robust_snr);
  const tqs = confidence_factor * content;

  return { tqs, persistence, linearity, robust_snr, confidence_factor };
}

/* NET-flat detection — implements P-trend-noise-gate v1.1.

   Q1. Absolute smallness — is the start→end delta smaller than the
       format's materiality floor? (e.g. a 0.30 change in a 10,000
       figure is numerically noise regardless of volatility.) Cheap
       short-circuit that short-circuits obvious flat cases before
       running the full TQS computation.

   Q2. Trend Quality Score < threshold. Per I-001, TQS combines path-
       directness (KER), linearity (R²), outlier-robust SNR (P5/P95),
       and sample-size confidence via geomean consensus. A low TQS
       means "not enough clear directional signal" — render grey.

   Either test passing ⇒ net flat. */
export function isNetFlat(values, format = 'currency') {
  if (!Array.isArray(values) || values.length < 2) return true;
  const nums = values.filter(v => typeof v === 'number' && Number.isFinite(v));
  if (nums.length < 2) return true;
  const first = nums[0];
  const last  = nums[nums.length - 1];
  const delta = Math.abs(last - first);

  /* Q1 — absolute smallness vs format materiality. */
  const magnitude = Math.max(Math.abs(first), Math.abs(last), 1);
  const floor = materialityFloor(format);
  const relative = magnitude * 0.005;                // 0.5% of typical value
  const threshold = Math.min(floor, Math.max(floor * 0.5, relative));
  if (delta <= threshold) return true;

  /* Q2 — I-001 Trend Quality Score. */
  const { tqs } = trendQualityScore(values, format);
  return tqs < TQS_FLAT_THRESHOLD;
}

/* v8 Path AE — seriesRange is now format-aware.
   When the series is materially flat the caller gets `flat:true` and
   the (min,max) is padded so any downstream sparkline renderer that
   still tries to draw has a non-zero denominator. Consumers should
   gate on `flat` and skip rendering entirely (cheaper than drawing
   a flatline the eye can't see). */
/* v8 Path AI — FAVORABILITY TONE (metric polarity).
   Industry context: there's no universal standard for "this metric
   going up is bad." The deepest financial pedigree comes from
   accounting variance analysis (CPA/CMA) which uses **Favorable (F)
   / Unfavorable (U)** — actual revenue > budget is favorable;
   actual expense < budget is also favorable. KPI / OKR frameworks
   call the same concept "polarity" or "directionality." Bloomberg
   and FactSet hardcode per-metric rules. We pick the accountant's
   vocabulary and call the schema attribute `betterWhen`:
     - 'higher' → rising = favorable (green), falling = unfavorable (red)
                  e.g. revenue, equity, savings rate, liquid ending
     - 'lower'  → falling = favorable (green), rising = unfavorable (red)
                  e.g. debt, expenses, DSO, debt-to-equity, expense ratio
     - 'neutral'→ tone stays neutral regardless of direction
                  e.g. salary net, AR, WHT+gov (driven by tax rules not
                  management action)

   Returns 'good' | 'bad' | 'neutral' matching MiniSparkline's tone
   palette. When `useSemantic` is false, falls back to the legacy
   "rising = good" heuristic so users can opt out (scenario toggle
   `sparklineSemanticTone`). */
/* v8 Path AI.2 — ROBUST TREND via linear regression slope.
   Previous heuristic: `nums[last] >= nums[0]` — just two points. For
   volatile series (Seasonal events with a -30K Dec spike, Lifestyle
   with one big month, Total operating expenses which absorbs all the
   seasonal/lifestyle noise) the two-point comparison lied about the
   actual trend direction. Example: a series that slopes gently upward
   overall but ends on a dip gets marked as "falling."

   Least-squares slope on uniform x-spacing reduces to:
     slope = Σᵢ(i - x̄)(yᵢ - ȳ) / Σᵢ(i - x̄)²
   Denominator is just Σᵢ(i - x̄)² = n(n²-1)/12 for i=0..n-1. Positive
   slope = rising trend, negative = falling, 0 = flat. Textbook.

   Returns the slope as a number. Consumers compare against zero for
   direction, or use magnitude if they care about steepness. */
export function seriesSlope(values) {
  if (!Array.isArray(values) || values.length < 2) return 0;
  const pts = values
    .map((v, i) => ({ i, v }))
    .filter(p => typeof p.v === 'number' && Number.isFinite(p.v));
  const n = pts.length;
  if (n < 2) return 0;
  const xMean = pts.reduce((s, p) => s + p.i, 0) / n;
  const yMean = pts.reduce((s, p) => s + p.v, 0) / n;
  let num = 0, den = 0;
  for (const p of pts) {
    const dx = p.i - xMean;
    num += dx * (p.v - yMean);
    den += dx * dx;
  }
  return den === 0 ? 0 : num / den;
}

export function computeTrendTone(values, betterWhen, useSemantic = false) {
  if (!Array.isArray(values) || values.length < 2) return 'good';
  const nums = values.filter(v => typeof v === 'number' && Number.isFinite(v));
  if (nums.length < 2) return 'good';
  /* v8 Path AI.2 — slope replaces first-vs-last. `rising` here means
     "the overall trend line slopes upward" — the interpretation a
     human expects when they eyeball a sparkline. First-vs-last was
     too fragile for volatile series (see the old-heuristic bug that
     showed Seasonal events as red when visible data was clearly
     trending up). */
  const slope = seriesSlope(values);
  const rising = slope >= 0;
  /* Clarified app rule (2026-04-22):
     - Semantic tone OFF, or betterWhen unknown, or betterWhen='neutral':
         fall back to DIRECTIONAL coloring (up=green, down=red). A
         strictly-linear rise on a "neutral-polarity" metric like
         Total L+E should still read green — the direction is the
         signal even when favorability is undeclared.
     - Grey is reserved for truly neutral cases, which in practice
         means net-flat / noise-dominated series. That logic lives in
         resolveTrendVisual (via isNetFlat), which overrides this
         tone when appropriate. */
  if (!useSemantic || !betterWhen || betterWhen === 'neutral') {
    return rising ? 'good' : 'bad';
  }
  // Semantic: apply metric polarity. Favorable = rising for 'higher',
  // favorable = falling for 'lower'. Unfavorable direction = 'bad'.
  const favorable = betterWhen === 'lower' ? !rising : rising;
  return favorable ? 'good' : 'bad';
}

/* v8 Path AK — MAGNITUDE-VIEW resolution for display-aligned sparklines.
   Filing-style tables render negative currency values in parentheses:
   an expense of 30,000 displays as "(30,000.00)". Users read this as
   a positive magnitude with a "subtraction" marker — their intuition
   when the number changes from (25,000) to (30,000) is "the number
   got bigger." But raw-value sparklines plot -25K → -30K, which
   visually slopes DOWN (because -30K is lower than -25K on the number
   line). The line direction visually contradicts the displayed numbers.

   Fix: for always-negative currency lines, flip to MAGNITUDE VIEW —
   plot absolute values, infer betterWhen='lower', force semantic
   tone for that line. Net effect:
     - Sparkline visually matches displayed numbers (bigger parens = up)
     - Color respects polarity (rising expense = red, falling = green)
     - Works identically whether Semantic tone toggle is ON or OFF

   This is purely presentational — the row's fmtAmount-rendered cells
   still show the raw negative values with parens. Only the sparkline
   underlay, MiniSparkline column, and delta badge use the magnitude
   series.

   Applies only to:
     - format === 'currency'       (ratios/pct/days/etc. show signs literally)
     - nums.every(n => n < 0)      (true expense / outflow lines, not mixed)
     - at least one non-zero value (pure-zero rows render flat em-dash)

   Positive-only lines (revenue, assets, equity, inflows) and mixed-sign
   lines (Net income, CFF) fall through unchanged. */
export function resolveTrendVisual(values, format, explicitBetterWhen, semanticTone = false) {
  const fallback = {
    series: values || [],
    rising: true,
    slope: 0,
    tone: 'good',
    flat: true,
    betterWhen: explicitBetterWhen,
    isMagnitude: false,
  };
  if (!Array.isArray(values) || values.length < 2) return fallback;

  let series = values;
  let betterWhen = explicitBetterWhen;
  let useSemanticEffective = semanticTone;
  let isMagnitude = false;

  if (format === 'currency') {
    const nonZero = values.filter(v => typeof v === 'number' && Number.isFinite(v) && v !== 0);
    const isAlwaysNegative = nonZero.length > 0 && nonZero.every(n => n < 0);
    if (isAlwaysNegative) {
      /* Flip to magnitude for display (parens-style currency reads as
         a positive number growing larger). `betterWhen` gets a 'lower'
         default so that IF semantic tone is ON, an expense magnitude
         rising correctly reads as unfavorable.

         Bug fix: we used to force useSemanticEffective = true here,
         which overrode the user's semanticTone=false setting. That meant
         an expense row was permanently polarity-aware even when the user
         wanted pure-directional coloring (up=green, down=red) across the
         whole app. Now the user toggle is the only thing that decides. */
      series = values.map(v => (typeof v === 'number' && Number.isFinite(v) ? Math.abs(v) : v));
      betterWhen = betterWhen ?? 'lower';
      isMagnitude = true;
    }
  }

  const slope = seriesSlope(series);
  const rising = slope >= 0;

  /* Two different kinds of "flat":

     strictlyFlat — peak-to-trough spread is below materiality. There
                    is literally nothing to draw (a perfectly flat line
                    or sub-pixel jiggle). Callers that check `flat`
                    skip rendering the sparkline entirely and show an
                    em-dash placeholder.

     netFlat      — series moves around meaningfully but the NET verdict
                    (start→end) is either tiny or noise-dominated. The
                    LINE should still render (user wants to see the
                    volatility) in NEUTRAL tone, and the arrow reads →
                    grey. This is the "mid-flight bumps, ends where it
                    started" case per the app-wide rule.

     We return `flat = strictlyFlat` so render-or-skip semantics stay
     intact. Net-flat rows still draw — they just wear neutral colors. */
  const strictlyFlat = isFlatSeries(series, format);
  const netFlat = isNetFlat(series, format);
  const tone = netFlat
    ? 'neutral'
    : computeTrendTone(series, betterWhen, useSemanticEffective);
  return { series, rising, slope, tone, flat: strictlyFlat, netFlat, betterWhen, isMagnitude };
}

export function seriesRange(values, format = 'currency') {
  if (!Array.isArray(values)) return { min: 0, max: 0, flat: true };
  const nums = values.filter(v => typeof v === 'number' && Number.isFinite(v));
  if (nums.length === 0) return { min: 0, max: 0, flat: true };
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const flat = isFlatSeries(values, format);
  if (flat) {
    // Pad the range with the format's floor so segment math doesn't /0
    const pad = materialityFloor(format);
    return { min: min - pad, max: max + pad, flat: true };
  }
  return { min, max, flat: false };
}
